// Serialize + save for the V2 assignment form (ADForm / AssignmentDashboardV2).
//
// The V2 form keeps a FLAT model (getBlankAssignmentForm in assignmentUtils.js)
// while /assignment expects the nested shape (projectId, assignmentDetails{...},
// schedule{...}, pricing{purchaseOrderId, workTypes[{customerPriceListId,...}]}).
// This module is the bridge in the form -> server direction; the inverse lives in
// parseAssignmentResponse.
//
// Deliberately separate from assignmentUtils.js: the legacy dashboard
// (AssignmentDashboard.vue + ADNewAssignmentModal) still saves through
// formatBackendData / processAssignmentsWithDatas, and nothing here touches that
// path. The payload shape below mirrors the one the spreadsheet upload posts
// (addAssignmentRowsToDB), which is the proven contract for /assignment.

// libraries
import moment from "moment";
// internal
import {
  processAssignment,
  getAssignments,
  getTasks,
  processTask,
} from "@/utils/newDbUtils";
import { publishMessage } from "@/utils/slackUtils";

// The blank form uses these sentinels instead of null, so "untouched" has to be
// detected rather than falsy-checked.
export const EMPTY_PROJECT_NAME = "Empty";
export const EMPTY_REQUESTER_NAME = "Empty";

// formatBackendData applies the same fallback, so a record created here matches
// one created from the legacy form.
export const DEFAULT_REQUEST_TIME = "10:00 AM";

// Same guards the legacy form applies before notifying Slack.
const PROJECTS_WITHOUT_EDIT_MODE_MESSAGE = [
  "Harman Product Marketing",
  "iTunes Film/TV Writeups",
  "iTunes - Apple Books",
];
const REQUEST_TYPES_WITHOUT_MESSAGE = [
  "Project Management",
  "Development",
  "Administrative",
];

export const isRealProject = (project) =>
  !!project?.id && project.name !== EMPTY_PROJECT_NAME;

// Assignees arrive either as a project staff object ({ user: { id } }) or, before
// syncAssigneesWithStaffList runs, as the flat user the /assignment payload
// carries ({ id }). Both have to resolve to the user id the API wants.
export const staffUserId = (person) => person?.user?.id ?? person?.id ?? null;

const requesterUserId = (requester) => requester?.user?.id ?? null;

// Departments come off the enum as { id, value }; a loaded assignment may carry
// either the object or the bare id.
const departmentId = (department) =>
  typeof department === "object" && department !== null
    ? department.id ?? null
    : department ?? null;

// -1 is the sentinel the API reads as "clear the purchase order". The form writes
// the picked PO to `purchaseOrder`; `pricing.purchaseOrder` is only ever set when
// an existing assignment is parsed, so both have to be consulted.
const purchaseOrderId = (form) => {
  const picked = form.purchaseOrder ?? form.pricing?.purchaseOrder ?? null;
  if (picked === -1) return -1;
  return picked?.id ?? null;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Dates land here as "YYYY-MM-DD" from the pickers, as a Date, or as the
// [year, month, day] array Spring serializes LocalDate into.
export const toApiDate = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    const [year, month, day] = value;
    if (!year || !month || !day) return null;
    return moment({ year: +year, month: +month - 1, day: +day }).format(
      "YYYY-MM-DD"
    );
  }
  const parsed = moment(
    value,
    value instanceof Date
      ? undefined
      : ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss", moment.ISO_8601]
  );
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

// The API stores times as "10:00 AM". The V2 pickers write 24-hour "HH:mm:00",
// and a parsed assignment can hand back the [hour, minute] array — so normalise
// all three instead of posting whatever the form happens to hold.
export const toApiTime = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) {
    const [hour, minute] = value;
    if (hour === undefined || hour === null) return null;
    return moment({ hour: +hour, minute: +(minute ?? 0) }).format("hh:mm A");
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = moment(raw, ["hh:mm A", "h:mm A", "HH:mm:ss", "HH:mm", "H:mm"]);
  return parsed.isValid() ? parsed.format("hh:mm A") : null;
};

// Pay period is a month: the picker gives "YYYY-MM", a parsed assignment can give
// "YYYY-MM-DD" or [year, month].
export const toApiPayPeriod = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    const [year, month] = value;
    if (!year || !month) return null;
    return `${year}-${String(month).padStart(2, "0")}`;
  }
  const parsed = moment(String(value), [
    "YYYY-MM",
    "YYYY-MM-DD",
    moment.ISO_8601,
  ]);
  return parsed.isValid() ? parsed.format("YYYY-MM") : null;
};

// Schedule values are stored as office wall clock, not as instants — the legacy
// form converts the client's time into this zone before saving (see
// convertDeliveryDue in ADNewAssignmentItem.vue).
export const OFFICE_TIME_ZONE = "America/New_York";

// "Now" as an office wall clock, anchored to UTC exactly the way
// parseFormDateTime anchors a stored date+time. Comparing a deadline against
// Date.now() instead would skew every countdown by the viewer's own UTC offset.
export const officeWallClockNow = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: OFFICE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const part = (type) => Number(parts.find((el) => el.type === type)?.value);
  // Some ICU builds report midnight as hour 24 under hour12: false.
  const hour = part("hour") % 24;
  return Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    hour,
    part("minute"),
    0,
    0
  );
};

// A form date + time as one instant, anchored to UTC.
//
// Both halves are wall-clock values — the record stores no offset — so they are
// read as UTC and must be rendered with timeZone: "UTC". A deadline then shows
// the hour that was actually picked instead of being shifted by wherever the
// viewer happens to be. Accepts every shape the form and the API produce:
// "YYYY-MM-DD" / [y,m,d] dates and "HH:mm:ss" / "hh:mm A" / [h,m] times.
// Returns null when there is no usable date.
export const parseFormDateTime = (date, time) => {
  const day = toApiDate(date);
  if (!day) return null;
  const [year, month, dayOfMonth] = day.split("-").map(Number);

  let hours = 0;
  let minutes = 0;
  const apiTime = toApiTime(time);
  if (apiTime) {
    const parsed = moment(apiTime, "hh:mm A");
    hours = parsed.hours();
    minutes = parsed.minutes();
  }

  return new Date(Date.UTC(year, month - 1, dayOfMonth, hours, minutes, 0, 0));
};

// Today in the office timezone — the same default the legacy blank form applies
// to requestDate. en-CA formats as YYYY-MM-DD, so the shifted date needs no
// reparsing (feeding a locale string back through moment without a format is what
// makes the legacy version log a deprecation warning).
const todayInOfficeTz = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

// A due date+time as one comparable instant. Missing times sort to midnight so a
// date-only comparison still orders correctly.
const dueMoment = (date, time) => {
  const day = toApiDate(date);
  if (!day) return null;
  const parsed = moment(
    `${day} ${toApiTime(time) || "12:00 AM"}`,
    "YYYY-MM-DD hh:mm A"
  );
  return parsed.isValid() ? parsed : null;
};

// Rows are held as { customerPriceList: {...}, quantity, orderPosition } (the
// shape ADLogWorkType writes); the API wants the price list id. Rows without a
// price list are dropped rather than posted as half-filled entries.
const buildWorkTypes = (workTypes, assignmentStatus) => {
  const rows = (workTypes || [])
    .filter((el) => el && (el.customerPriceList || el.customerPriceListId))
    .map((el, index) => ({
      customerPriceListId:
        el.customerPriceList?.id ?? el.customerPriceListId ?? null,
      quantity: toNumberOrNull(el.quantity),
      orderPosition: el.orderPosition ?? index + 1,
    }));
  if (!rows.length) return null;
  // "Pending Hours" records are created before the hours are known, and the API
  // rejects a null quantity on them (same coercion processAssignmentsWithDatas
  // applies).
  if (assignmentStatus === "Pending Hours" && rows[0].quantity === null) {
    rows[0].quantity = 0;
  }
  return rows;
};

// Flat V2 form -> /assignment request body.
export const buildAssignmentPayload = (form) => {
  const project = form.relatedProject;
  const assignmentStatus = form.status || null;

  return {
    projectId: isRealProject(project) ? project.id : null,
    clientId: project?.client?.id ?? null,
    clientRequesterId: requesterUserId(form.clientRequester),
    projectManagerId:
      form.projectManager?.id ?? project?.projectManager?.id ?? null,
    primaryId: staffUserId(form.primary),
    secondaryId: staffUserId(form.secondary),
    qcId: staffUserId(form.qc),
    department: departmentId(form.department),
    task: {
      taskId: form.taskId || null,
      id: form.taskDbId ?? null,
    },
    assignmentDetails: {
      // External (client-side) assignment id — distinct from the Cue id.
      assignmentId: form.externalAssignmentId || null,
      assignmentDetail: form.assignmentDetail || null,
      assignmentTitle: form.assignmentTitle || null,
      assignmentStatus,
      billed: form.isBilled ?? null,
      characterCount: toNumberOrNull(form.characterCount),
      clientFeedback: form.clientFeedback ?? null,
      clientReviewed: form.clientReviewed ?? null,
      contentId: form.contentId || null,
      emailSubjectLine: form.emailSubjectLine || null,
      fileLocation: form.fileLocation || null,
      genre: form.genre || null,
      geo: form.geo || null,
      keyword: form.keyword || null,
      // The record holds a single link; the tab collects a list, so the first one
      // is the one that persists (see the note in ADForm.publishAssignment).
      link: form.links?.[0] || null,
      mediaType: form.mediaType || null,
      note: form.note || null,
      primaryAccepted: form.primaryAccepted ?? null,
      secondaryAccepted: form.secondaryAccepted ?? null,
      requestType: form.requestType || null,
      sourceGeo: form.sourceGeo || null,
      slackTs: form.slackTs || null,
      taskId: form.taskId || null,
      urgent: form.urgent ?? null,
      wordCount: toNumberOrNull(form.wordCount),
    },
    pricing: {
      purchaseOrderId: purchaseOrderId(form),
      workTypes: buildWorkTypes(form.pricing?.workTypes, assignmentStatus),
    },
    schedule: {
      deliveryDate: toApiDate(form.deliveryDate),
      deliveryTime: toApiTime(form.deliveryTime),
      payPeriod: toApiPayPeriod(form.payPeriod),
      requestDate: toApiDate(form.requestDate) || todayInOfficeTz(),
      requestTime: toApiTime(form.requestTime) || DEFAULT_REQUEST_TIME,
      primaryDueDate: toApiDate(form.schedule?.primaryDueDate),
      primaryDueTime: toApiTime(form.schedule?.primaryDueTime),
      secondaryDueDate: toApiDate(form.schedule?.secondaryDueDate),
      secondaryDueTime: toApiTime(form.schedule?.secondaryDueTime),
      qcDueDate: toApiDate(form.schedule?.qcDueDate),
      qcDueTime: toApiTime(form.schedule?.qcDueTime),
    },
  };
};

// Everything the API needs before it will accept the record, plus the ordering
// rules the legacy form enforces. Returns display-ready messages (empty = valid)
// so the caller can surface them all at once instead of failing on a 400.
export const validateAssignmentForm = (form) => {
  const messages = [];
  const missing = [];

  if (!isRealProject(form.relatedProject)) missing.push("Project");
  if (!form.requestType) missing.push("Request Type");
  if (!form.geo) missing.push("Target GEO");
  if (!staffUserId(form.primary)) missing.push("Primary");
  if (!toApiDate(form.deliveryDate)) missing.push("Delivery Date");
  if (!toApiPayPeriod(form.payPeriod)) missing.push("Pay Period");

  if (missing.length)
    messages.push(`Missing required field(s): ${missing.join(", ")}.`);

  // The form can waive the deadline chain explicitly.
  if (!form.overrideDueDates) {
    const primaryDue = dueMoment(
      form.schedule?.primaryDueDate,
      form.schedule?.primaryDueTime
    );
    const secondaryDue = dueMoment(
      form.schedule?.secondaryDueDate,
      form.schedule?.secondaryDueTime
    );
    const qcDue = dueMoment(
      form.schedule?.qcDueDate,
      form.schedule?.qcDueTime
    );

    if (primaryDue && secondaryDue && !secondaryDue.isAfter(primaryDue))
      messages.push("Secondary Due Date cannot be before Primary Due Date.");
    if (secondaryDue && qcDue && !qcDue.isAfter(secondaryDue))
      messages.push("QC Due Date cannot be before Secondary Due Date.");
    if (!secondaryDue && primaryDue && qcDue && !qcDue.isAfter(primaryDue))
      messages.push("QC Due Date cannot be before Primary Due Date.");
  }

  // Same rule the dashboard's bulk status change and ADStatusSelector apply:
  // this project's adaptations cannot be delivered without a billable quantity.
  if (
    form.relatedProject?.id === 115 &&
    form.requestType === "Adaptation" &&
    form.status === "Delivered" &&
    (!toNumberOrNull(form.wordCount) ||
      !toNumberOrNull(form.pricing?.workTypes?.[0]?.quantity))
  ) {
    messages.push(
      "Word Count and Work Type quantity are required before this assignment can be Delivered."
    );
  }

  return messages;
};

// POST a new assignment / PUT an existing one. Never throws: processAssignment
// resolves the error response, so the caller gets a flag plus the response to
// hand to the flash message.
export const saveAssignmentForm = async ({ form }) => {
  const isUpdate = !!form.id;
  const payload = buildAssignmentPayload(form);
  const response = await processAssignment({
    data: payload,
    id: isUpdate ? form.id : undefined,
  });

  if (!response || response.status > 204) {
    return { ok: false, isUpdate, payload, response, id: form.id ?? null };
  }

  return {
    ok: true,
    isUpdate,
    payload,
    response,
    saved: response.data ?? null,
    id: response.data?.id ?? form.id ?? null,
  };
};

// Notify Slack the same way the legacy form does, under the same guards. Never
// rethrows: a Slack outage must not make a saved assignment look unsaved.
export const notifyAssignmentSaved = async ({
  form,
  payload,
  assignmentId,
  isUpdate,
}) => {
  try {
    const project = form.relatedProject;
    const details = payload.assignmentDetails;

    if (REQUEST_TYPES_WITHOUT_MESSAGE.includes(details.requestType)) return;
    // These projects only announce brand-new records.
    if (PROJECTS_WITHOUT_EDIT_MODE_MESSAGE.includes(project?.name) && isUpdate)
      return;
    if (project?.dubEditorProject) return;
    // Updates to iTunes FC records are not announced.
    if (isUpdate && project?.name === "iTunes FC") return;

    const slackIdKey = `slackId${project?.name === "iTunes FC" ? "Fc" : "Staff"}`;
    const tag = (person) => {
      if (!person) return null;
      const slackId = person.adminDetails?.[slackIdKey];
      if (slackId) return `<@${slackId}>`;
      const first = person.user?.firstName ?? person.firstName;
      const last = person.user?.lastName ?? person.lastName;
      return first ? `${first} ${last ?? ""}`.trim() : null;
    };

    const geoEntry = { assignments: [details.assignmentDetail] };
    const writer = tag(form.primary);
    const editor = tag(form.secondary);
    const reviewer = tag(form.qc);
    if (writer) geoEntry.writers = [writer];
    if (editor) geoEntry.editors = [editor];
    if (reviewer) geoEntry.reviewers = [reviewer];

    await publishMessage({
      mode:
        project?.name === "iTunes FC" ? "fcNotification" : "nonFcNotification",
      item: {
        ...payload,
        project,
        primary: form.primary,
        secondary: form.secondary,
        qc: form.qc,
        assignment: details.assignmentDetail,
        geos: [{ [details.geo]: geoEntry }],
        id: assignmentId,
      },
      // Swiss-German adaptations get their own copy.
      modifier:
        !isUpdate &&
        details.geo === "de-CH" &&
        details.requestType === "Adaptation"
          ? "de-CH-adaptation"
          : undefined,
    });
  } catch (err) {
    console.error("Assignment Slack notification failed:", err);
  }
};

// Promote the parent task once every assignment under it reached `status`.
//
// Keyed off the cueTaskId the API returns on the saved record. The legacy form
// rebuilt this key by concatenating a moment object with the task id, which
// stringifies to a full date and never matches — so this reads the value the
// server already computed instead of recomputing it.
export const syncParentTaskStatus = async ({ cueTaskId, status }) => {
  try {
    if (!cueTaskId || !status) return;

    const assignmentsResp = await getAssignments({
      query: { search: cueTaskId },
    });
    const siblings = (assignmentsResp?.content || []).filter(
      (el) => el.cueTaskId === cueTaskId
    );
    if (
      !siblings.length ||
      !siblings.every(
        (el) => el.assignmentDetails?.assignmentStatus === status
      )
    )
      return;

    const tasksResp = await getTasks({ query: { search: cueTaskId } });
    const task = (tasksResp?.content || []).find(
      (el) => el.cueTaskId === cueTaskId
    );
    if (!task) return;

    await processTask({
      data: { taskStatus: status },
      id: task.id,
      patch: true,
    });
  } catch (err) {
    console.error("Parent task status sync failed:", err);
  }
};
